import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useCallback } from "react";

export const usePlants = (propertyId = null) => {
    const queryClient = useQueryClient();

    // 1. Fetching Logic (Query)
    const { data: plants = [], isLoading: loading, refetch } = useQuery({
        queryKey: ["plants", propertyId],
        queryFn: async () => {
            let query = supabase
                .from("plants")
                .select("*")
                .order("created_at", { ascending: false });

            if (propertyId) {
                query = query.eq("property_id", propertyId);
            }

            const { data, error } = await query;
            if (error) throw error;
            return data || [];
        },
        enabled: true,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });

    // 2. Addition Mutation
    const addMutation = useMutation({
        mutationFn: async (plantData) => {
            const { data, error } = await supabase
                .from("plants")
                .insert([{
                    ...plantData,
                    property_id: propertyId
                }])
                .select()
                .single();

            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["plants", propertyId] });
            toast.success("Plant added to your collection");
        },
        onError: (error) => {
            console.error("Error adding plant:", error);
            toast.error("Failed to add plant");
        }
    });

    // 3. Update Mutation
    const updateMutation = useMutation({
        mutationFn: async ({ id, updates }) => {
            const { property_id: _property_id, ...validUpdates } = updates;
            const { data, error } = await supabase
                .from("plants")
                .update(validUpdates)
                .eq("id", id)
                .select()
                .single();

            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["plants", propertyId] });
            toast.success("Plant details updated");
        },
        onError: (error) => {
            console.error("Error updating plant:", error);
            toast.error("Failed to update plant");
        }
    });

    // 4. Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const { error } = await supabase
                .from("plants")
                .delete()
                .eq("id", id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["plants", propertyId] });
            toast.success("Plant removed");
        },
        onError: (error) => {
            console.error("Error deleting plant:", error);
            toast.error("Failed to delete plant");
        }
    });

    // 5. Water Plant Mutation
    const waterMutation = useMutation({
        mutationFn: async ({ plantId, customDate }) => {
            const plant = plants.find(p => p.id === plantId);
            if (!plant) throw new Error("Plant not found");

            // Use UTC midnight to avoid timezone shifts
            const lastWatered = customDate ? new Date(customDate) : new Date();
            lastWatered.setUTCHours(0, 0, 0, 0);

            const nextWatered = new Date(lastWatered);
            if (plant.watering_unit === 'days') {
                nextWatered.setUTCDate(nextWatered.getUTCDate() + plant.watering_frequency);
            } else {
                nextWatered.setUTCDate(nextWatered.getUTCDate() + (plant.watering_frequency * 7));
            }

            const { error } = await supabase
                .from("plants")
                .update({
                    last_watered_at: lastWatered.toISOString(),
                    next_watering_at: nextWatered.toISOString()
                })
                .eq("id", plantId);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["plants", propertyId] });
            toast.success("Plant watered!");
        },
        onError: (error) => {
            console.error("Error marking plant as watered:", error);
            toast.error("Failed to update plant");
        }
    });

    return {
        plants,
        loading,
        addPlant: (plantData) => addMutation.mutateAsync(plantData).then(() => true).catch(() => false),
        updatePlant: (id, updates) => updateMutation.mutateAsync({ id, updates }).then(() => true).catch(() => false),
        deletePlant: (id) => deleteMutation.mutateAsync(id).then(() => true).catch(() => false),
        markAsWatered: (plantId, customDate = null) => waterMutation.mutateAsync({ plantId, customDate }).then(() => true).catch(() => false),
        refetch
    };
};
